
import React, { useEffect, useRef, useState } from 'react';
import { SidebarPortal, BlockDataForm } from '@plone/volto/components';
import { flattenToAppURL } from '@plone/volto/helpers';
import schema from './schema';
import './multicard.css';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';

const getImageURL = (image) => {
  if (typeof image === 'string') {
    return `${flattenToAppURL(image)}/@@images/image/large`;
  }
  const imageItem = Array.isArray(image) ? image[0] : image;
  if (imageItem?.['@id']) return `${flattenToAppURL(imageItem['@id'])}/@@images/image/large`;
  if (imageItem?.download) return flattenToAppURL(imageItem.download);
  return null;
};

const getHref = (url, anchor) => {
  const base = typeof url === 'string' ? url : (Array.isArray(url) ? url[0]?.['@id'] : url?.['@id']);
  return anchor?.trim() ? (base ? `${base}${anchor}` : anchor) : base || '#';
};

const isExternalLink = (href) => {
  if (!href) return false;
  if (typeof window === 'undefined') return false;
  try {
    const host = window.location.hostname;
    return href.startsWith('http') && !href.includes(host);
  } catch {
    return false;
  }
};

const CountUp = ({ end = 0, duration = 1500, formatK = false }) => {
  const [value, setValue] = useState(0);
  const startRef = useRef(null);
  const endRef = useRef(Number(end) || 0);

  useEffect(() => {
    endRef.current = Number(end) || 0;
    setValue(0);
    startRef.current = null;

    const step = (ts) => {
      if (!startRef.current) startRef.current = ts;
      const progress = Math.min((ts - startRef.current) / duration, 1);
      let next = Math.floor(progress * endRef.current);
      if (formatK && endRef.current >= 1000) {
        // snap to thousands for a clear stepping effect
        next = next - (next % 1000);
      }
      setValue(next);
      if (progress < 1) requestAnimationFrame(step);
    };
    const raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [end, duration]);

  return (
    <span className="stat-value-number">
      {formatK && endRef.current >= 1000
        ? `${Math.max(1, Math.floor(value / 1000)).toLocaleString()}k`
        : value.toLocaleString()}
    </span>
  );
};

const useIsMobile = () => {
  const [isMobile, setIsMobile] = React.useState(false);
  React.useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener ? mq.addEventListener('change', update) : mq.addListener(update);
    return () => (mq.removeEventListener ? mq.removeEventListener('change', update) : mq.removeListener(update));
  }, []);
  return isMobile;
};

const MobileCarousel = ({ enabled, autoplay=true, showDots=true, children }) => {
  const isMobile = useIsMobile();
  const plugins = React.useMemo(() => (autoplay ? [Autoplay({ delay: 3500, stopOnMouseEnter: true })] : []), [autoplay]);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true }, plugins);
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const slides = React.Children.toArray(children);

  React.useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    emblaApi.on('select', onSelect);
    onSelect();
  }, [emblaApi]);

  if (!enabled || !isMobile) return <>{children}</>;
  return (
    <div className="embla">
      <div className="embla__viewport" ref={emblaRef}>
        <div className="embla__container">
          {slides.map((slide, i) => (
            <div className="embla__slide" key={i}>{slide}</div>
          ))}
        </div>
      </div>
      {showDots && (
        <div className="embla__dots">
          {slides.map((_, i) => (
            <button
              key={i}
              className={`embla__dot ${i === selectedIndex ? 'is-selected' : ''}`}
              onClick={() => emblaApi && emblaApi.scrollTo(i)}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const Edit = ({ data, block, onChangeBlock, selected }) => {
  const alignmentClass = `align-${data.alignment || 'left'}`;
  const columnClass = `columns-${data.columns || 3}`;
  const useStatistics = !!data.useStatistics;

  return (
    <>
      {!useStatistics && (
        <div className={`multi-card-container ${alignmentClass} ${columnClass}`}>
          <MobileCarousel enabled={!!data.mobileCarousel} autoplay={data.mobileCarouselAutoplay !== false} showDots={data.mobileCarouselDots !== false}>
            {(data.items || []).map((item, index) => {
              const imageUrl = getImageURL(item.image);
              const href = getHref(item.url, item.anchor);
              const external = isExternalLink(href);
              return (
                <div
                  key={index}
                  className="multi-card"
                  style={{ backgroundImage: imageUrl ? `url(${imageUrl})` : 'none' }}
                >
                  <div className="multi-card-overlay" />
                  <div className="multi-card-content">
                    {item.title?.trim() && <h3>{item.title}</h3>}
                    {item.description?.trim() && <p>{item.description}</p>}
                    {item.linkText?.trim() && (
                      <a
                        href={href}
                        target={external ? '_blank' : '_self'}
                        rel={external ? 'noopener noreferrer' : undefined}
                      >
                        {item.linkText}
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </MobileCarousel>
        </div>
      )}

      {useStatistics && (
        <div className={`multi-card-container stats ${alignmentClass} ${columnClass}`}>
          <MobileCarousel enabled={!!data.mobileCarousel} autoplay={data.mobileCarouselAutoplay !== false} showDots={data.mobileCarouselDots !== false}>
            {(data.statistics || []).map((stat, idx) => {
              const href = getHref(stat.url, stat.anchor);
              const external = isExternalLink(href);
              const content = (
                <div className="multi-stat" key={idx}>
                  {stat.label?.trim() && <div className="stat-label">{stat.label}</div>}
                  <div className="stat-value">
                    <CountUp end={stat.value} duration={data.statAnimationMs || 6000} formatK={!!data.statsFormatK} />
                    {stat.suffix?.trim() && <span className="stat-suffix">{stat.suffix}</span>}
                  </div>
                  {stat.extraInfo?.trim() && <div className="stat-extra">{stat.extraInfo}</div>}
                </div>
              );
              return stat.url || stat.anchor ? (
                <a
                  key={idx}
                  className="multi-stat-link"
                  href={href}
                  target={external ? '_blank' : '_self'}
                  rel={external ? 'noopener noreferrer' : undefined}
                >
                  {content}
                </a>
              ) : (
                content
              );
            })}
          </MobileCarousel>
        </div>
      )}

      {selected && (
        <SidebarPortal selected={selected}>
          <BlockDataForm
            schema={schema(data)}
            title="Multi Card Block"
            onChangeField={(id, value) =>
              onChangeBlock(block, { ...data, [id]: value })
            }
            block={block}
            formData={data}
          />
        </SidebarPortal>
      )}
    </>
  );
};

export default Edit;
